/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useMemo } from 'react';
import { Filepond } from '@pypestream/design-system';

interface FileUploadProps {
  addFile: (file: File) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIcon = (
  fileName: string
): 'file-pdf' | 'file-doc' | 'file-txt' | 'file-odt' | 'data' => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'file-pdf';
    case 'doc':
    case 'docx':
      return 'file-doc';
    case 'txt':
      return 'file-txt';
    case 'odt':
      return 'file-odt';
    default:
      return 'data';
  }
};

const ACCEPTED_FILE_TYPES = [
  'text/html',
  'application/json',
  'application/x-ndjson',
  'text/x-markdown',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'text/plain',
  '.md',
  '.ppt',
  '.pptx',
  '.docx',
  '.doc',
  '.odt',
  '.odp',
  '.json',
  '.ndjson',
];

export const useKnowledgeAgentUpload = ({ addFile }: FileUploadProps) => {
  const onFileSelect = useCallback(
    ({
      files,
    }: {
      url?: string;
      pictureId?: string;
      data?: unknown;
      files?: (File | undefined)[];
    }) => {
      files?.forEach((file) => {
        if (file) {
          addFile(file);
        }
      });
    },
    [addFile]
  );

  const customFileUpload = useMemo(
    () => (
      <Filepond
        test-id="create-solution-modal-file-upload"
        accept={ACCEPTED_FILE_TYPES}
        allowMultiple
        immediatelyUpload={false}
        // @ts-ignore
        onChange={({ files }) => onFileSelect({ files: files || [] })}
        previewLabel="Drag & drop files here"
        previewCta="Select a file"
      />
    ),
    [onFileSelect]
  );

  return { formatFileSize, getFileIcon, customFileUpload };
};
